import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Calculator, Send, RotateCcw, Boxes, Save, Trash2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Domain constants ------------------------------------------------------
// Per-flute paper weight uplift (corrugation length factor).
const FLUTE_MULTIPLIER = { A: 1.5, B: 1.3, C: 1.42, E: 1.2, F: 1.15 };

// Flute combinations available per ply count. The string encodes flute letters
// in flute-layer order: 5 Ply "B/C" => Flute 1 = B, Flute 2 = C.
const FLUTE_COMBOS_BY_PLY = {
  3: ["A", "B", "C", "E", "F"],
  5: ["A/A", "B/B", "C/C", "B/C", "C/B", "A/B", "B/A", "A/C", "C/A", "E/E", "E/F", "F/E", "F/F"],
  7: ["A/B/A", "A/C/A", "B/A/B", "C/B/C"],
};

const SHADE_OPTIONS = [
  { code: "NS", label: "NS — Natural Shade" },
  { code: "GYT", label: "GYT — Glazed Yellow Tissue" },
  { code: "D", label: "D — Duplex" },
  { code: "IMP", label: "IMP — Imported" },
  { code: "K", label: "K — Kraft" },
];
// Shades that do NOT use a Bursting Factor in their product code.
// e.g. Duplex / Imported are typically stocked as IMP150, D200 — without BF.
const NO_BF_SHADES = new Set(["D", "IMP"]);
const shadeNeedsBf = (shade) => !NO_BF_SHADES.has(shade);
const PLY_OPTIONS = [3, 5, 7];
const JOINT_OPTIONS = [
  { value: "Single", multiplier: 1 },
  { value: "Double", multiplier: 2 },
  { value: "Four", multiplier: 4 },
];
const DEFAULT_UPS_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12];
// NOTE: localStorage below stores only non-sensitive UI state
// (saved calculator UP options and named presets). No PII, tokens, or
// credentials are ever stored here.
const CUSTOM_UPS_KEY = "cpi_inv_ctrl_custom_ups";
const loadCustomUps = () => {
  try {
    const raw = localStorage.getItem(CUSTOM_UPS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((n) => Number.isInteger(n) && n > 0) : [];
  } catch (e) {
    console.error("Custom UPs load failed:", e);
    return [];
  }
};
const saveCustomUps = (list) => {
  try {
    localStorage.setItem(CUSTOM_UPS_KEY, JSON.stringify(list));
  } catch (e) {
    console.error("Custom UPs save failed:", e);
  }
};

// Convert any input unit to inches (calculation base unit).
const toInches = (val, unit) => {
  const n = parseFloat(val) || 0;
  if (unit === "MM") return n / 25.4;
  if (unit === "Cm") return n / 2.54;
  return n;
};

// Build the layer skeleton for a given ply + flute combination.
// Returns: [{ kind: "liner"|"flute", label, fluteLetter? }, ...]
const buildLayerTemplate = (ply, fluteCombo) => {
  const parts = (fluteCombo || "").split("/").map((s) => s.trim().toUpperCase()).filter(Boolean);
  if (ply === 3) {
    const f = parts[0] || "";
    return [
      { kind: "liner", label: "Liner 1" },
      { kind: "flute", label: "Flute 1", fluteLetter: f },
      { kind: "liner", label: "Liner 2" },
    ];
  }
  if (ply === 5) {
    return [
      { kind: "liner", label: "Liner 1" },
      { kind: "flute", label: "Flute 1", fluteLetter: parts[0] || "" },
      { kind: "liner", label: "Liner 2" },
      { kind: "flute", label: "Flute 2", fluteLetter: parts[1] || "" },
      { kind: "liner", label: "Liner 3" },
    ];
  }
  if (ply === 7) {
    return [
      { kind: "liner", label: "Liner 1" },
      { kind: "flute", label: "Flute 1", fluteLetter: parts[0] || "" },
      { kind: "liner", label: "Liner 2" },
      { kind: "flute", label: "Flute 2", fluteLetter: parts[1] || "" },
      { kind: "liner", label: "Liner 3" },
      { kind: "flute", label: "Flute 3", fluteLetter: parts[2] || "" },
      { kind: "liner", label: "Liner 4" },
    ];
  }
  return [];
};

const blankLayer = () => ({ shade: "NS", gsm: "" });

// ---- Presets (localStorage) ------------------------------------------------
const PRESETS_KEY = "cpi_inv_ctrl_presets";
const loadPresets = () => {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    console.error("Preset load failed:", e);
    return [];
  }
};
const savePresets = (list) => {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(list));
  } catch (e) {
    console.error("Preset save failed:", e);
  }
};
const newPresetId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `p_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const InventoryControl = () => {
  const navigate = useNavigate();

  // --- Section 1: Top fields -----------------------------------------------
  const [unit, setUnit] = useState("Inches");
  const [reelSize, setReelSize] = useState("");
  const [cuttingSize, setCuttingSize] = useState("");
  const [requiredQty, setRequiredQty] = useState("");
  const [jointType, setJointType] = useState("Single");
  const [ups, setUps] = useState(1);

  // --- Section 2: Board specifications -------------------------------------
  const [ply, setPly] = useState(3);
  const [fluteCombo, setFluteCombo] = useState(FLUTE_COMBOS_BY_PLY[3][0]);
  const [bf, setBf] = useState("");

  // --- Section 3: Per-layer shade & GSM ------------------------------------
  // layers[i] = { shade, gsm }, ordered as buildLayerTemplate(ply, fluteCombo)
  const [layers, setLayers] = useState(() =>
    buildLayerTemplate(3, FLUTE_COMBOS_BY_PLY[3][0]).map(() => blankLayer())
  );

  // --- Presets state -------------------------------------------------------
  const [presets, setPresets] = useState(() => loadPresets());
  const [presetSelectId, setPresetSelectId] = useState("");
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  // --- Custom UPs state (persisted in localStorage, merged with defaults) --
  const [customUps, setCustomUps] = useState(() => loadCustomUps());
  const [addUpsOpen, setAddUpsOpen] = useState(false);
  const [newUpsValue, setNewUpsValue] = useState("");
  const upsOptions = useMemo(() => {
    const merged = Array.from(new Set([...DEFAULT_UPS_OPTIONS, ...customUps]));
    return merged.sort((a, b) => a - b);
  }, [customUps]);

  // --- Materials from Store DB (RM Name source of truth) -------------------
  const [materials, setMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);

  const fetchMaterials = async () => {
    setMaterialsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/materials`);
      setMaterials(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch materials:", e);
      toast.error("Failed to load Store materials");
    } finally {
      setMaterialsLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  // Case-insensitive lookup of materials by product_code.
  const materialByCode = useMemo(() => {
    const m = new Map();
    materials.forEach((mat) => {
      if (mat?.product_code) m.set(String(mat.product_code).toLowerCase(), mat);
    });
    return m;
  }, [materials]);

  const lookupRm = (shade, gsm, bfNum) => {
    if (!shade || !gsm) return null;
    // For Duplex / Imported, BF is not part of the product code.
    if (!shadeNeedsBf(shade)) {
      const candidate = `${shade}${gsm}`.toLowerCase();
      return materialByCode.get(candidate) || null;
    }
    if (!bfNum) return null;
    const candidate = `${shade}${gsm}${bfNum}`.toLowerCase();
    return materialByCode.get(candidate) || null;
  };

  // When ply changes manually, reset flute combo to the first valid option for that ply
  // and resize per-layer state to match the new template length.
  // When ply is set as part of a preset load, this effect is skipped so the
  // preset's combo/layers/bf survive.
  const skipPlyEffect = useRef(false);
  useEffect(() => {
    if (skipPlyEffect.current) {
      skipPlyEffect.current = false;
      return;
    }
    const validCombos = FLUTE_COMBOS_BY_PLY[ply] || [];
    const nextCombo = validCombos[0] || "";
    setFluteCombo(nextCombo);
    const tpl = buildLayerTemplate(ply, nextCombo);
    setLayers((prev) => {
      const next = tpl.map((_, i) => prev[i] || blankLayer());
      return next;
    });
  }, [ply]);

  // When flute combo changes for the same ply, the layer count is unchanged,
  // so we keep existing shade/gsm values (only flute-multiplier mapping changes).

  const layerTemplate = useMemo(() => buildLayerTemplate(ply, fluteCombo), [ply, fluteCombo]);

  const updateLayer = (idx, patch) => {
    setLayers((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  // --- Computed: layer rows + totals ---------------------------------------
  // Boards required = ceil(Qty × Joint multiplier / UPs)
  // Joint = how many boards combine into one box (Single=1, Double=2, Four=4)
  // UPs   = how many boxes are produced from one board (1, 2, 3, ...)
  // Per-board paper weight is raw Reel × Cutting × Effective GSM (no Joint factor).
  const calc = useMemo(() => {
    const jointMultiplier = (JOINT_OPTIONS.find((j) => j.value === jointType) || {}).multiplier || 1;
    const reelInches = toInches(reelSize, unit);
    const cuttingInches = toInches(cuttingSize, unit);
    const bfNum = parseInt(bf, 10) || 0;
    const upsNum = parseInt(ups, 10) || 1;
    const reqQty = parseFloat(requiredQty) || 0;
    const numBoards = upsNum > 0 ? Math.ceil((reqQty * jointMultiplier) / upsNum) : 0;

    const rows = layerTemplate.map((tpl, idx) => {
      const layerState = layers[idx] || blankLayer();
      const baseGsm = parseFloat(layerState.gsm) || 0;
      const fluteLetter = tpl.fluteLetter || "";
      const fluteMult = tpl.kind === "flute" ? FLUTE_MULTIPLIER[fluteLetter] || 1 : 1;
      const effectiveGsm = tpl.kind === "flute" ? baseGsm * fluteMult : baseGsm;
      // Paper weight per board (kg): Reel × Cutting × Effective GSM / 1550 / 1000
      const paperWeight = reelInches * cuttingInches * effectiveGsm / 1550 / 1000;
      // Reel weight (kg): Paper Weight × Boards (no extra /1000 — paperWeight is already kg)
      const reelWeightKg = paperWeight * numBoards;
      const shadeCode = layerState.shade || "NS";
      const needsBf = shadeNeedsBf(shadeCode);
      const hasInputs = baseGsm > 0 && (needsBf ? bfNum > 0 : true);
      const rmCandidate = hasInputs
        ? (needsBf ? `${shadeCode}${baseGsm}${bfNum}` : `${shadeCode}${baseGsm}`)
        : "";
      const matchedMaterial = rmCandidate ? lookupRm(shadeCode, baseGsm, bfNum) : null;
      const rmName = matchedMaterial ? matchedMaterial.product_code : "";
      const rmFound = !!matchedMaterial;
      return {
        sNo: idx + 1,
        kind: tpl.kind,
        label: tpl.label,
        fluteLetter,
        fluteMult,
        baseGsm,
        effectiveGsm,
        rmName,
        rmCandidate,
        rmFound,
        materialId: matchedMaterial?.id || null,
        paperWeightGm: paperWeight,
        reelWeightKg,
      };
    });
    const totalBoardWeight = rows.reduce((s, r) => s + r.paperWeightGm, 0);
    const totalReelWeightKg = rows.reduce((s, r) => s + r.reelWeightKg, 0);
    return { rows, totalBoardWeight, numBoards, totalReelWeightKg, jointMultiplier, reelInches };
  }, [unit, reelSize, cuttingSize, requiredQty, jointType, ups, bf, layerTemplate, layers, materialByCode]);

  const reset = () => {
    setReelSize(""); setCuttingSize(""); setRequiredQty("");
    setJointType("Single"); setUps(1);
    setBf("");
    setPly(3);  // useEffect will reset combo + layers
    toast.info("Form cleared");
  };

  // Apply the same shade or same GSM to all liner / all flute layers.
  const fillAll = (kind, patch) => {
    setLayers((prev) => prev.map((l, i) => (layerTemplate[i]?.kind === kind ? { ...l, ...patch } : l)));
  };

  // --- Preset handlers -----------------------------------------------------
  const handleSavePreset = () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Please enter a preset name"); return; }
    if (presets.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
      toast.error("A preset with this name already exists");
      return;
    }
    const preset = {
      id: newPresetId(),
      name,
      ply,
      fluteCombo,
      bf,
      layers: layers.map((l) => ({ shade: l.shade, gsm: l.gsm })),
      savedAt: new Date().toISOString(),
    };
    const next = [...presets, preset];
    setPresets(next);
    savePresets(next);
    setPresetName("");
    setSavePresetOpen(false);
    setPresetSelectId(preset.id);
    toast.success(`Preset "${name}" saved`);
  };

  const handleLoadPreset = (id) => {
    if (!id) {
      setPresetSelectId("");
      return;
    }
    setPresetSelectId(id);
    const allPresets = loadPresets();
    const p = allPresets.find((x) => x.id === id) || presets.find((x) => x.id === id);
    if (!p) {
      toast.error("Preset not found");
      return;
    }
    skipPlyEffect.current = true;
    setPly(p.ply);
    setFluteCombo(p.fluteCombo);
    setBf(p.bf || "");
    const tpl = buildLayerTemplate(p.ply, p.fluteCombo);
    const nextLayers = tpl.map((_, i) => p.layers[i] || blankLayer());
    setLayers(nextLayers);
    toast.success(`Loaded preset "${p.name}"`);
  };

  const handleDeletePreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    const next = presets.filter((x) => x.id !== id);
    setPresets(next);
    savePresets(next);
    if (presetSelectId === id) setPresetSelectId("");
    toast.info(`Deleted preset "${p.name}"`);
  };

  // --- Custom UPs handlers -------------------------------------------------
  const handleAddCustomUps = () => {
    const n = parseInt(newUpsValue, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Please enter a positive whole number");
      return;
    }
    if (upsOptions.includes(n)) {
      toast.info(`${n} UP is already in the list — selecting it`);
      setUps(n);
      setAddUpsOpen(false);
      setNewUpsValue("");
      return;
    }
    const next = Array.from(new Set([...customUps, n])).sort((a, b) => a - b);
    setCustomUps(next);
    saveCustomUps(next);
    setUps(n);
    setAddUpsOpen(false);
    setNewUpsValue("");
    toast.success(`Added ${n} UP`);
  };

  const handleUpsChange = (value) => {
    if (value === "__add__") {
      setNewUpsValue("");
      setAddUpsOpen(true);
      return;
    }
    setUps(parseInt(value, 10));
  };

  // Send merged line items to the PO Form. Same RM Name across layers
  // (e.g. Liner1 and Liner2 are both NS12016) gets merged into one row.
  // Only DB-matched RMs are sent — unmatched layers are blocked with an alert.
  const sendToPO = () => {
    if (!calc.rows.length || calc.totalBoardWeight <= 0) {
      toast.error("Please fill Reel, Cutting and all GSMs (and BF for non-Imported/Duplex shades)");
      return;
    }
    if (calc.numBoards <= 0) {
      toast.error("Required Quantity must be > 0");
      return;
    }
    const unmatched = calc.rows.filter((r) => r.rmCandidate && !r.rmFound);
    if (unmatched.length > 0) {
      const list = Array.from(new Set(unmatched.map((r) => r.rmCandidate))).join(", ");
      toast.error(
        `${unmatched.length} layer(s) have no matching RM in the Store database: ${list}. Please add them in Store → Database first.`
      );
      return;
    }
    // Aggregate by RM Name → total reel weight in kg across all boards
    const merged = new Map();
    calc.rows.forEach((r) => {
      if (!r.rmFound || !r.rmName) return;
      const totalKg = r.paperWeightGm * calc.numBoards;
      const existing = merged.get(r.rmName) || { rmName: r.rmName, totalKg: 0, layers: [], materialId: r.materialId };
      existing.totalKg += totalKg;
      existing.layers.push(r.label);
      merged.set(r.rmName, existing);
    });
    const items = Array.from(merged.values()).map((m) => ({
      rmName: m.rmName,
      description: `${m.rmName} (${m.layers.join(", ")})`,
      qtyKg: +m.totalKg.toFixed(3),
      materialId: m.materialId || null,
    }));
    if (!items.length) {
      toast.error("No valid line items to send");
      return;
    }
    sessionStorage.setItem("cpi_inv_control_payload", JSON.stringify({
      items,
      meta: {
        unit, reelSize, cuttingSize, requiredQty, jointType, ups,
        ply, fluteCombo, bf,
        layers: layers.map((l, i) => ({ ...l, label: layerTemplate[i]?.label })),
        totalBoardWeight: calc.totalBoardWeight,
        numBoards: calc.numBoards,
      },
      createdAt: Date.now(),
    }));
    toast.success(`Sent ${items.length} line item(s) to PO Form`);
    navigate("/store/purchase-order/new?prefill=inv-control");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={() => navigate("/store/purchase-order/new")} className="text-gray-400 hover:text-white -ml-2 mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to PO Form
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Boxes className="h-6 w-6 text-amber-400" /> Inventory Control
            </h1>
            <p className="text-sm text-gray-400">Calculate board weight per piece and total raw-material requirement.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="inv-ctrl-reset">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
            <Button onClick={sendToPO} className="bg-amber-600 hover:bg-amber-700" data-testid="inv-ctrl-send-to-po">
              <Send className="h-4 w-4 mr-1" /> Send to PO Form
            </Button>
          </div>
        </div>

        {/* SECTION 1 — Top fields */}
        <Card className="bg-gray-900/60 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-cyan-300 text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" /> Order Dimensions
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inches">Inches</SelectItem>
                  <SelectItem value="MM">Millimeters (MM)</SelectItem>
                  <SelectItem value="Cm">Centimeters (Cm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Reel Size / Width</Label>
              <Input type="number" min={0} step="0.01" value={reelSize} onChange={(e) => setReelSize(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-reel-size" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Cutting Size / Length</Label>
              <Input type="number" min={0} step="0.01" value={cuttingSize} onChange={(e) => setCuttingSize(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-cutting-size" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Required Quantity (boxes)</Label>
              <Input type="number" min={0} value={requiredQty} onChange={(e) => setRequiredQty(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-req-qty" />
            </div>
          </CardContent>
          <CardContent className="pt-0 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Joint Type</Label>
              <Select value={jointType} onValueChange={setJointType}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-joint">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {JOINT_OPTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.value} (×{j.multiplier})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">UPs</Label>
              <Select value={String(ups)} onValueChange={handleUpsChange}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-ups">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {upsOptions.map(u => (
                    <SelectItem key={u} value={String(u)}>
                      {u} UP{customUps.includes(u) ? <span className="text-cyan-400 text-xs ml-1">(custom)</span> : null}
                    </SelectItem>
                  ))}
                  <SelectItem value="__add__" className="text-cyan-300">
                    + Add custom UP…
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end">
              <p className="text-xs text-gray-400">
                <span className="text-gray-500">Reel: </span>
                <span className="text-cyan-300 font-mono">{calc.reelInches.toFixed(2)} in</span>
                <span className="text-gray-500 mx-2">·</span>
                <span className="text-gray-500">Boards needed: </span>
                <span className="text-cyan-300 font-mono" data-testid="inv-ctrl-boards-needed">{calc.numBoards}</span>
                <span className="text-gray-500 ml-2">(Qty × Joint{calc.jointMultiplier > 1 ? `×${calc.jointMultiplier}` : ""} ÷ UPs)</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 2 — Board Specifications: Ply + Flute Combo + BF */}
        <Card className="bg-gray-900/60 border-gray-700">
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <CardTitle className="text-cyan-300 text-base">Board Specifications</CardTitle>
                <p className="text-xs text-gray-500">Pick the Ply, then the Flute Combination valid for that ply. Each layer's Shade &amp; GSM is set below.</p>
              </div>
              {/* Presets bar */}
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[220px]">
                  <Label className="text-gray-300 text-xs">Load Preset</Label>
                  <div className="flex gap-1 mt-1">
                    <select
                      value={presetSelectId}
                      onChange={(e) => handleLoadPreset(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                      data-testid="inv-ctrl-preset-select"
                    >
                      <option value="">{presets.length ? "Choose preset…" : "No presets yet"}</option>
                      {presets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.ply}P · {p.fluteCombo})
                        </option>
                      ))}
                    </select>
                    {presetSelectId && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeletePreset(presetSelectId)}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 shrink-0"
                        data-testid="inv-ctrl-preset-delete"
                        title="Delete selected preset"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => { setPresetName(""); setSavePresetOpen(true); }}
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20"
                  data-testid="inv-ctrl-preset-save-btn"
                >
                  <Save className="h-4 w-4 mr-1" /> Save as Preset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Ply</Label>
              <Select value={String(ply)} onValueChange={(v) => setPly(parseInt(v, 10))}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-ply">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLY_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p} Ply</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Flute Combination</Label>
              <Select value={fluteCombo} onValueChange={setFluteCombo}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-flute-combo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(FLUTE_COMBOS_BY_PLY[ply] || []).map((c) => {
                    const letters = c.split("/");
                    const labelExtras = letters
                      .map((l) => `${l}=×${FLUTE_MULTIPLIER[l] || 1}`)
                      .join(", ");
                    return (
                      <SelectItem key={c} value={c}>
                        {c} &nbsp;<span className="text-gray-500 text-xs">({labelExtras})</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Bursting Factor (BF)
                <span className="text-gray-500 ml-1">(skip for IMP / Duplex)</span>
              </Label>
              <Input type="number" min={0} value={bf} onChange={(e) => setBf(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-ctrl-bf" />
            </div>
          </CardContent>
        </Card>

        {/* SECTION 3 — Per-layer Shade & GSM editor */}
        <Card className="bg-gray-900/60 border-cyan-700/40">
          <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-cyan-300 text-base">Layer Inputs</CardTitle>
              <p className="text-xs text-gray-500">Set Shade and GSM for every layer of the board.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-gray-500 self-center">Quick fill:</span>
              <Select onValueChange={(v) => fillAll("liner", { shade: v })}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-300 h-8 w-44" data-testid="inv-ctrl-quickfill-liner-shade">
                  <SelectValue placeholder="All liners shade…" />
                </SelectTrigger>
                <SelectContent>
                  {SHADE_OPTIONS.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select onValueChange={(v) => fillAll("flute", { shade: v })}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-300 h-8 w-44" data-testid="inv-ctrl-quickfill-flute-shade">
                  <SelectValue placeholder="All flutes shade…" />
                </SelectTrigger>
                <SelectContent>
                  {SHADE_OPTIONS.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-transparent">
                  <TableHead className="text-gray-400 text-xs w-16">S No</TableHead>
                  <TableHead className="text-gray-400 text-xs">Layer</TableHead>
                  <TableHead className="text-gray-400 text-xs w-44">Shade</TableHead>
                  <TableHead className="text-gray-400 text-xs w-32">GSM</TableHead>
                  <TableHead className="text-gray-400 text-xs">Flute</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {layerTemplate.map((tpl, idx) => {
                  const layer = layers[idx] || blankLayer();
                  return (
                    <TableRow key={idx} className="border-gray-800" data-testid={`inv-ctrl-input-row-${idx + 1}`}>
                      <TableCell className="text-gray-300 text-sm">{idx + 1}</TableCell>
                      <TableCell className={`text-sm ${tpl.kind === "flute" ? "text-amber-300" : "text-emerald-300"}`}>
                        {tpl.label}
                      </TableCell>
                      <TableCell>
                        <Select value={layer.shade} onValueChange={(v) => updateLayer(idx, { shade: v })}>
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-8" data-testid={`inv-ctrl-shade-${idx + 1}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SHADE_OPTIONS.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          value={layer.gsm}
                          onChange={(e) => updateLayer(idx, { gsm: e.target.value })}
                          className="bg-gray-800 border-gray-600 text-white h-8"
                          data-testid={`inv-ctrl-gsm-${idx + 1}`}
                        />
                      </TableCell>
                      <TableCell className="text-xs font-mono text-gray-400">
                        {tpl.kind === "flute"
                          ? <span className="text-amber-300">{tpl.fluteLetter || "—"} <span className="text-gray-500">(×{FLUTE_MULTIPLIER[tpl.fluteLetter] || 1})</span></span>
                          : <span className="text-gray-600">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* SECTION 4 — Computed Layer Specification */}
        <Card className="bg-gray-900/60 border-emerald-700/40">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-emerald-300 text-base">Layer Specification</CardTitle>
                <p className="text-xs text-gray-400">RM Name is matched against the Store materials database. Paper Weight per board = Reel × Cutting × Effective GSM / 1550 / 1000. Reel Weight = Paper Weight × Boards.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {materialsLoading ? "Loading…" : `${materials.length} RM in DB`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchMaterials}
                  disabled={materialsLoading}
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-8"
                  data-testid="inv-ctrl-refresh-materials"
                  title="Reload materials from Store database"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${materialsLoading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-transparent">
                  <TableHead className="text-gray-400 text-xs w-16">S No</TableHead>
                  <TableHead className="text-gray-400 text-xs">Layer</TableHead>
                  <TableHead className="text-gray-400 text-xs">RM Name (from DB)</TableHead>
                  <TableHead className="text-gray-400 text-xs">Effective GSM</TableHead>
                  <TableHead className="text-gray-400 text-xs text-right">Paper Weight (kg)</TableHead>
                  <TableHead className="text-gray-400 text-xs text-right">Reel Weight (kg)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calc.rows.map((row) => (
                  <TableRow key={row.sNo} className="border-gray-800" data-testid={`inv-ctrl-row-${row.sNo}`}>
                    <TableCell className="text-gray-300 text-sm">{row.sNo}</TableCell>
                    <TableCell className={`text-sm ${row.kind === "flute" ? "text-amber-300" : "text-emerald-300"}`}>
                      {row.label}{row.kind === "flute" && row.fluteLetter ? ` (${row.fluteLetter})` : ""}
                    </TableCell>
                    <TableCell className="text-sm font-mono" data-testid={`inv-ctrl-rmname-${row.sNo}`}>
                      {row.rmFound ? (
                        <span className="text-cyan-300">{row.rmName}</span>
                      ) : row.rmCandidate ? (
                        <span className="inline-flex items-center gap-1 text-red-400" title="No matching RM in Store database">
                          <AlertTriangle className="h-3 w-3" />
                          {row.rmCandidate}
                          <span className="text-[10px] text-red-500/80 ml-1">(not in DB)</span>
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-300 text-sm font-mono">
                      {row.baseGsm}
                      {row.kind === "flute" && row.baseGsm
                        ? ` × ${row.fluteMult} = ${row.effectiveGsm.toFixed(0)}`
                        : ""}
                    </TableCell>
                    <TableCell className="text-gray-200 text-sm font-mono text-right">{row.paperWeightGm.toFixed(3)}</TableCell>
                    <TableCell className="text-amber-300 text-sm font-mono text-right" data-testid={`inv-ctrl-reel-weight-${row.sNo}`}>
                      {row.reelWeightKg.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-gray-700 bg-emerald-500/5">
                  <TableCell colSpan={4} className="text-emerald-300 text-sm font-bold text-right">Total (per board / for {calc.numBoards} boards):</TableCell>
                  <TableCell className="text-emerald-300 text-sm font-bold font-mono text-right" data-testid="inv-ctrl-total-board-weight">
                    {calc.totalBoardWeight.toFixed(3)} kg
                  </TableCell>
                  <TableCell className="text-amber-300 text-sm font-bold font-mono text-right" data-testid="inv-ctrl-total-reel-weight">
                    {calc.totalReelWeightKg.toFixed(3)} kg
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {calc.rows.some((r) => r.rmCandidate && !r.rmFound) && (
              <div className="mt-3 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-xs text-red-200" data-testid="inv-ctrl-missing-rm-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-red-300 mb-1">Some RM names are not in the Store database</p>
                    <p>Add the missing materials in <span className="text-cyan-300">Store → Database</span> first, then click <b>Refresh</b> above. Send to PO Form is disabled until every layer is matched.</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/30" data-testid="inv-ctrl-preset-save-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-300">Save Spec as Preset</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Saves the current Ply, Flute Combination, BF and per-layer Shade/GSM as a named preset for one-click reuse later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-gray-300 text-xs">Preset name</Label>
            <Input
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g. 5-Ply B/C 120 GSM 16BF"
              className="bg-gray-800 border-gray-600 text-white"
              data-testid="inv-ctrl-preset-name-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSavePreset(); } }}
            />
            <p className="text-xs text-gray-500">
              Will store: <span className="text-cyan-300">{ply} Ply · {fluteCombo}</span> · BF {bf || "—"} · {layers.length} layer GSM/shade values.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetOpen(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSavePreset} className="bg-cyan-600 hover:bg-cyan-700" data-testid="inv-ctrl-preset-save-confirm">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom UP Dialog */}
      <Dialog open={addUpsOpen} onOpenChange={setAddUpsOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/30" data-testid="inv-ctrl-add-ups-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-300">Add Custom UP value</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">
              Enter a whole number. New values are saved on this device and appear in the UPs dropdown for future use.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-gray-300 text-xs">Number of UPs</Label>
            <Input
              type="number"
              min={1}
              step={1}
              value={newUpsValue}
              onChange={(e) => setNewUpsValue(e.target.value)}
              placeholder="e.g. 7, 9, 15, 20"
              className="bg-gray-800 border-gray-600 text-white"
              data-testid="inv-ctrl-add-ups-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCustomUps(); } }}
              autoFocus
            />
            {customUps.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-gray-500 mb-1">Custom UPs you've added:</p>
                <div className="flex flex-wrap gap-1">
                  {customUps.map((n) => (
                    <span key={n} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
                      {n} UP
                      <button
                        type="button"
                        onClick={() => {
                          const next = customUps.filter((x) => x !== n);
                          setCustomUps(next);
                          saveCustomUps(next);
                          if (ups === n) setUps(1);
                        }}
                        className="text-red-300 hover:text-red-400"
                        aria-label={`Remove ${n} UP`}
                        data-testid={`inv-ctrl-remove-custom-up-${n}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUpsOpen(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleAddCustomUps} className="bg-cyan-600 hover:bg-cyan-700" data-testid="inv-ctrl-add-ups-confirm">
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryControl;
